import { useCallback, useEffect, useRef } from 'react';
import { message } from 'antd';

import useMountedRef from './useMountedRef';
import { findTutorial } from './tutorialSteps';
import { waitForStepElement } from './tourEngine';
import { onTutorialActionResult } from './tutorialOutcomes';
import {
  MAX_TUTORIAL_RESUMES,
  clearTutorialSession,
  isTutorialComplete,
  rememberTutorialSession,
  takeTutorialCompletion,
  takeTutorialSession,
} from './tutorialProgress';
import { captureAnalytics } from '../../actions/preload';

/**
 * A tour that is *running*: launching one, ending one, and picking one back up
 * after the page was torn down underneath it.
 *
 * Split out of LearnMedsender alongside useTutorialProgress (which owns what the
 * user has completed) so the container itself is wiring and render.
 */
const useTutorialLifecycle = ({
  tutorials,
  activeTutorial,
  activeTutorialId,
  pendingTutorialId,
  progress,
  impersonating,
  history,
  location,
  completeTutorial,
  beginTutorialRun,
  clearPendingTutorial,
  openTutorialMenu,
  startTutorial,
  stopTutorial,
}) => {
  const mountedRef = useMountedRef();
  const startedPathRef = useRef(null);
  /**
   * Which launch attempt is current.
   *
   * `start()` awaits `waitForStepElement` for up to 8s, and `mountedRef` cannot
   * bound that wait because this container is mounted app-wide and never unmounts.
   * So a user who launched A (first target absent), gave up, and launched B had
   * A's wait resolve into a live B: it called `stopTutorial()` and showed A's
   * "isn't available" message, killing B mid-tour. TutorialRunner guards this same
   * class with `driverRef.current !== driverObj` in three places; `start()` had no
   * equivalent. Bumped on every launch; a stale attempt sees the mismatch and
   * stands down.
   */
  const launchGenRef = useRef(0);
  // Aborts the previous launch's first-target poll so a superseded attempt stops
  // querying the DOM instead of running out its full 8s budget.
  const startAbortRef = useRef(null);
  /**
   * Whether the running tutorial's first target has actually been found.
   *
   * `start()` puts the id into redux BEFORE awaiting that target (it has to — the
   * checklist must close so it cannot cover the target), so `activeTutorialId` is
   * live for up to 8s while nothing is on screen. Without this the route-change
   * effect below treated navigating during that window as abandoning a running
   * tour: a `tutorial_exited / navigated_away` for a tutorial that never started,
   * inflating exit counts for orgs whose first target is slow.
   */
  const firstTargetConfirmedRef = useRef(false);

  /* ------------------------------------------------------------ route changes */

  // Navigating away mid-tutorial would leave the spotlight pointing at an
  // element that no longer exists, so end the run. TutorialRunner's own unmount
  // cleanup removes the overlay.
  useEffect(() => {
    if (impersonating) return;
    if (!activeTutorialId) return;
    if (startedPathRef.current && location.pathname !== startedPathRef.current) {
      // Some tutorials legitimately navigate mid-run (split-document opens the
      // record viewer); a declared prefix keeps the tour alive there.
      const allowedPrefixes = (activeTutorial && activeTutorial.allowedPathPrefixes) || [];
      if (allowedPrefixes.some((prefix) => location.pathname.startsWith(prefix))) return;
      // Stand down a first-target wait that is still in flight. Without this the
      // stale `start()` resolves AFTER the user has deliberately navigated away and
      // then shows its "isn't available on this screen" message and reopens the
      // checklist over wherever they went.
      launchGenRef.current += 1;
      if (startAbortRef.current) startAbortRef.current.abort();
      captureAnalytics('tutorial_exited', {
        tutorial: activeTutorialId,
        // Distinguished, not suppressed: `tutorial_started` has already been sent,
        // so dropping the exit would dangle the funnel. This says which of the two
        // things happened.
        reason: firstTargetConfirmedRef.current ? 'navigated_away' : 'navigated_away_before_start',
      });
      // Navigating away IS a deliberate exit, so the resume marker must go with
      // it. Leaving it armed meant the next page load in the tab silently
      // relaunched the tutorial the user had just walked out of — TutorialRunner's
      // unmount teardown is deliberately silent, so `exit()` (which does clear it)
      // never runs on this path.
      clearTutorialSession();
      stopTutorial();
    }
  }, [impersonating, location.pathname, activeTutorialId, activeTutorial, stopTutorial]);

  /* ----------------------------------------------------------------- handlers */

  const start = useCallback(async (tutorialId, { resumes = 0 } = {}) => {
    if (impersonating) return;
    const tutorial = findTutorial(tutorials, tutorialId);
    if (!tutorial) return;

    launchGenRef.current += 1;
    const launch = launchGenRef.current;
    if (startAbortRef.current) startAbortRef.current.abort();
    const startController = typeof AbortController === 'function' ? new AbortController() : null;
    startAbortRef.current = startController;
    firstTargetConfirmedRef.current = false;
    beginTutorialRun();

    captureAnalytics('tutorial_started', { tutorial: tutorial.id });

    // Tutorials are screen-specific; take the user there first.
    if (tutorial.path && location.pathname !== tutorial.path) {
      history.push(tutorial.path);
    }
    startedPathRef.current = tutorial.path || location.pathname;

    // Close the checklist immediately so it can't cover the first target.
    startTutorial(tutorial.id);
    // Survive a teardown caused by the action being taught (archive reloads the
    // page, upload navigates away). `resumes` carries how many times we have
    // already come back this way, so the resume cannot recur without bound.
    rememberTutorialSession(tutorial.id, { resumes });

    const firstTarget = await waitForStepElement(tutorial.steps[0], {
      signal: startController && startController.signal,
    });
    if (!mountedRef.current) return;
    // A later launch has superseded this one — say nothing and touch nothing.
    if (launchGenRef.current !== launch) return;
    firstTargetConfirmedRef.current = !!firstTarget;
    if (!firstTarget) {
      captureAnalytics('tutorial_unavailable', { tutorial: tutorial.id });
      message.info(
        tutorial.unavailableReason
        || 'This tutorial isn\'t available on this screen right now. Try again once the page has loaded.',
      );
      // The marker was armed eight lines up for a tour that never began.
      clearTutorialSession();
      stopTutorial();
      openTutorialMenu();
    }
  }, [
    impersonating,
    tutorials,
    location.pathname,
    history,
    mountedRef,
    beginTutorialRun,
    startTutorial,
    stopTutorial,
    openTutorialMenu,
  ]);

  /**
   * Completes a tutorial the moment the action it teaches actually succeeds.
   *
   * Some taught actions tear the page down: a successful single upload ends in
   * `window.open('/app/view/:id', '_self')` (UploadSingleRecord), a hard
   * navigation that destroys the JS context. The tour's own teardown hooks never
   * get to run, so the popover just vanishes mid-step and nothing is recorded —
   * the user completes the task and the checklist still shows it unfinished.
   *
   * The listener is deliberately synchronous: `markTutorialComplete` writes
   * localStorage while `tutorialOutcomeMiddleware` is still inside the redux
   * dispatch — strictly before the effect that navigates away. Anything deferred
   * to a timer, a promise or a React re-render would lose that race, which is why
   * the outcome does not travel through redux STATE.
   */
  useEffect(() => {
    if (impersonating) return undefined;
    const completesOn = activeTutorial && activeTutorial.completesOn;
    if (!completesOn) return undefined;

    return onTutorialActionResult((action, success) => {
      if (action !== completesOn) return;
      // A failed action is not a completed lesson; leave the tour running so the
      // user can retry.
      if (!success) return;
      completeTutorial(activeTutorial.id);
    });
  }, [impersonating, activeTutorial, completeTutorial]);

  /**
   * A required step's target never showed up, so the tour gave up.
   *
   * Previously this was silent: the user clicked Next, nothing happened for up to
   * eight seconds, and then the tutorial simply disappeared. Changing an inbox
   * filter is enough to trigger it — the list refetches, so the record row the
   * next step needs is briefly absent — and it reads as "Next is broken".
   */
  const reportUnavailableStep = useCallback((step) => {
    captureAnalytics('tutorial_step_unavailable', {
      tutorial: activeTutorialId,
      step: step && step.title,
    });
    message.info(
      step && step.unavailableHint
        ? step.unavailableHint
        : 'That step needs something that isn\'t on screen yet. Nothing was changed — pick the tutorial again when the page has finished loading.',
    );
    clearTutorialSession();
    stopTutorial();
    openTutorialMenu();
  }, [activeTutorialId, stopTutorial, openTutorialMenu]);

  /**
   * A step's gate refused to advance — the page isn't in the state the next step
   * needs (the assign tutorial's selection is a reply, or more than one record).
   * Unlike `reportUnavailableStep` this does NOT end the tour: the user fixes the
   * selection and presses Next again, so only the reason is shown.
   */
  const reportBlockedStep = useCallback((reason) => {
    captureAnalytics('tutorial_step_blocked', { tutorial: activeTutorialId });
    message.info(reason);
  }, [activeTutorialId]);

  // `reason` is passed by the runner so the analytics record says what actually
  // happened. It used to be hardcoded 'user_exited', which asserted a user
  // decision for a failed action, a timed-out action and a missing required step
  // alike — three different diagnoses collapsed into the wrong one.
  const exit = useCallback((tutorialId, reason = 'user_exited') => {
    // A deliberate exit is not an interruption — do not resurrect it on reload.
    clearTutorialSession();
    captureAnalytics('tutorial_exited', { tutorial: tutorialId || activeTutorialId, reason });
    stopTutorial();
  }, [activeTutorialId, stopTutorial]);

  /**
   * Picks a tutorial back up after the page was torn down underneath it.
   *
   * Archiving ends in `window.location.reload()` (UploadedRecords) and a
   * successful upload in `window.open(..., '_self')`, so the user performs the
   * action they were being taught and the tour vanishes with the page. Redux is
   * gone, so `activeTutorialId` and `menuOpen` cannot carry across; the
   * sessionStorage marker written by `start` can.
   *
   * Two outcomes, because they want different things:
   * - Already complete (the normal case — the taught action is what completed it,
   *   and progress was written synchronously before the teardown): just reopen
   *   the checklist so the user sees the new checkmark rather than nothing.
   * - Still incomplete (the reload was incidental): restart that tutorial. From
   *   the beginning, honestly — the post-reload DOM has none of the state a
   *   mid-tour step assumed, so resuming at step N would spotlight the wrong
   *   thing. The tutorials are 2-4 steps, so this is cheap.
   *
   * `takeTutorialSession` clears as it reads, so this can fire at most once per
   * page load and cannot loop if the resume itself triggers another teardown.
   */
  const resumeEvaluatedRef = useRef(false);
  useEffect(() => {
    if (resumeEvaluatedRef.current) return;
    // Gated on impersonation ONLY, deliberately.
    //
    // This used to also require `userProfileLoaded && userEmail` so progress
    // would be read for the right key. Both were permanently falsy (see
    // mapStateToProps), so the effect never ran at all and archiving still lost
    // the checklist. The marker is per-tab and needs no identity to honour; the
    // worst case if the email is genuinely unknown is that a completed tutorial
    // is offered again, which is recoverable — silently doing nothing was not.
    if (impersonating) return;
    // Do not fight an in-flight tutorial (e.g. a Guide launch already running) —
    // and check that BEFORE consuming anything. Both `take*` helpers clear as they
    // read, so testing `activeTutorialId` afterwards threw the marker away: a
    // just-earned completion was silently discarded with no way to recover it.
    if (activeTutorialId) return;
    resumeEvaluatedRef.current = true;

    // A tutorial that FINISHED and then had the page pulled out from under it
    // (archive reloads, upload navigates). Reopen the checklist so the user lands
    // on their new checkmark instead of a bare page. Checked first: this is the
    // common case, since the taught action is what completes the tutorial.
    const justCompleted = takeTutorialCompletion();
    if (justCompleted) {
      captureAnalytics('tutorial_menu_opened', { source: 'completed_before_reload' });
      openTutorialMenu();
      return;
    }

    const interrupted = takeTutorialSession();
    if (!interrupted) return;

    const { tutorialId, resumes } = interrupted;

    // Already finished, or we have bounced back here too many times: show the
    // checklist rather than relaunching. Never leave the user with nothing.
    if (isTutorialComplete(progress, tutorialId) || resumes >= MAX_TUTORIAL_RESUMES) {
      openTutorialMenu();
      return;
    }
    captureAnalytics('tutorial_resumed', { tutorial: tutorialId, resumes });
    start(tutorialId, { resumes: resumes + 1 });
  }, [
    impersonating,
    activeTutorialId,
    progress,
    start,
    openTutorialMenu,
  ]);

  // A launch requested elsewhere (the Guide page's tutorial cards) is executed
  // here, because only the container has the router and the target-waiting logic
  // that starting a tutorial needs.
  useEffect(() => {
    if (!pendingTutorialId) return;
    // Gated on impersonation like every other entry path. This was the ONE that
    // was not, and it is the reachability chain for the cross-account write: the
    // Guide page's Start button parked an id here, this effect put a live tutorial
    // into redux, and that armed the completion listener against the impersonated
    // user. Clear the request either way so it cannot sit and fire later.
    clearPendingTutorial();
    if (impersonating) return;
    start(pendingTutorialId);
  }, [impersonating, pendingTutorialId, start, clearPendingTutorial]);

  return {
    start,
    exit,
    reportUnavailableStep,
    reportBlockedStep,
  };
};

export default useTutorialLifecycle;
